import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getFromSession, getLodgiqCookie } from '../app/util';

/**
 * Custom React hook to check if a user is logged in.
 * Optionally skips the check based on the `checkLoginEnabled` flag.
 *
 * @param {boolean} checkLoginEnabled - Whether to perform the login check.
 * @returns {Object} - { response, loading, error }
 */
const useCheckLogin = (checkLoginEnabled = true) => {
    const [response, setResponse] = useState(null);       // Holds login status (true/false)
    const [loading, setLoading] = useState(checkLoginEnabled); // Manages loading state
    const [error, setError] = useState(null);             // Holds any error that may occur
    const dispatch = useDispatch();
    const API_BASE_URL = process.env.REACT_APP_API_URL;

    useEffect(() => {

        // if master calsss exist in session storage then we do not need to make check login again.
        if(sessionStorage.getItem('master_class')){
            setResponse(true); 
            setLoading(false);
            setError(false); 
            return;
        }

        // If login check is disabled, set loading to false and response to false
        if (!checkLoginEnabled) {
            setResponse(false);
            setLoading(false);
            return;
        }

        // Async function to perform the login check
        const checkLogin = async () => {
            const token = localStorage.getItem('token');

            // If no token is found in localStorage, user is not logged in
            if (!token) {
                setResponse(false);
                setLoading(false);
                return;
            }

            // Check if the tab is among the "openTabs"
            const openTabs = JSON.parse(localStorage.getItem('openTabs') || '[]');
            // Proceed only if login check is enabled and there are open tabs
            if (checkLoginEnabled && openTabs && openTabs.length > 1) {
                try {
                    // Call the API to validate the login session
                    let apiUrl = API_BASE_URL + "users/check-login";
                    const res = await fetch(apiUrl, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': 'Token ' + token,
                        },
                    });

                    if (res.ok) {
                        const data = await res.json(); // Not used, but available
                        sessionStorage.setItem('token', localStorage.getItem('token'));

                        // Attempt to read user state from localStorage
                        let master_class = localStorage.getItem('master_class');
                        if (master_class) {
                            let master_class_json = JSON.parse(master_class);

                            // Dispatch Redux actions to update user and app state
                            dispatch({
                                type: "LOGIN_SUCCESS",
                                payload: { user: master_class_json.auth.user },
                            });
                            dispatch({
                                type: "PROFILE_SUCCESS",
                                payload: master_class_json.auth.profile,
                            });
                            dispatch({
                                type: "UPDATE_STATE",
                                payload: {
                                    app_state: master_class_json.app.current_state.app_state,
                                    ui_state: master_class_json.app.current_state.ui_state,
                                },
                            });

                            // Save state to sessionStorage for persistence
                            sessionStorage.setItem('master_class', master_class);
                            setResponse(true); // Mark login as successful
                        }
                    } else {
                        // If token is invalid or expired, clear all storage
                        localStorage.clear();
                        sessionStorage.clear();
                    }
                } catch (err) {
                    setError(err); // Capture any network or fetch errors
                } finally {
                    setLoading(false); // End loading either way
                }
            }else{
                setLoading(false);
                setError(false); 
            }
        };

        checkLogin(); // Execute the login check
    }, [checkLoginEnabled]);

    // Return login check result, loading state, and error
    return { response, loading, error };
};

export default useCheckLogin;
